import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import ReactGA from "react-ga4";
import axiosInstance from "../axios";

import "./Header.css";
import Navigation from "./navigation";



/**
 * Determines if a URL is an external link (starts with http:// or https://).
 */
const isExternalLink = (url) => {
  return url && (url.startsWith("http://") || url.startsWith("https://"));
};

/**
 * Renders a single menu link item using NavLink (for internal) or <a> (for external).
 */
const MenuLink = ({ item }) => {
  if (!item.url) return <span>{item.label}</span>;

  if (isExternalLink(item.url)) {
    return (
      <a
        href={item.url}
        target={item.open_in_new_tab ? "_blank" : "_self"}
        rel={item.open_in_new_tab ? "noopener noreferrer" : undefined}
      >
        {item.label}
      </a>
    );
  }

  return (
    <NavLink
      to={item.url}
      target={item.open_in_new_tab ? "_blank" : undefined}
      rel={item.open_in_new_tab ? "noopener noreferrer" : undefined}
    >
      {item.label}
    </NavLink>
  );
};

/**
 * Recursively renders a menu item and its children.
 */
const MenuItem = ({ item }) => {
  // Sub-dropdown (nested inside a dropdown)
  if (item.type === "sub-dropdown") {
    return (
      <li className="sub-dropdown">
        <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {item.label}
          {item.icon && (
            <img src={item.icon} alt="icon" style={{ height: "15px", width: "15px", transform: "rotate(-90deg)" }} />
          )}
        </span>
        {item.children && item.children.length > 0 && (
          <ul className="sub-dropdown-content">
            {item.children.map((child) => (
              <MenuItem key={child.id} item={child} />
            ))}
          </ul>
        )}
      </li>
    );
  }

  // Dropdown (top-level dropdown with children)
  if (item.type === "dropdown") {
    // Determine special CSS class for signin/register buttons
    let extraClass = "";
    if (item.group_name === "signin") extraClass = " signin-btn";
    if (item.group_name === "register") extraClass = " register-btn";

    return (
      <li className={`dropdown${extraClass}`}>
        <span>
          {item.label} {item.icon && <img src={item.icon} alt="icon" style={{ height: "15px", width: "15px" }} />}
        </span>
        {item.children && item.children.length > 0 && (
          <ul className="dropdown-content">
            {item.children.map((child) => (
              <MenuItem key={child.id} item={child} />
            ))}
          </ul>
        )}
      </li>
    );
  }

  // Regular nav item (leaf node)
  return (
    <li className={item.is_highlighted ? "highlighted" : ""}>
      <MenuLink item={item} />
    </li>
  );
};

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [menuLoading, setMenuLoading] = useState(true);

  ReactGA.initialize(process.env.REACT_APP_GA_TRACKING_ID);

  useEffect(() => {
    setCurrentPath(() => window.location.pathname);
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname,
      title: "Home Page",
    });

    // ✅ Check login status
    const token = localStorage.getItem("homeshiksha-tutor-jwt");
    setIsLoggedIn(!!token);

    // ✅ Fetch dynamic menu items from API
    const fetchMenuItems = async () => {
      try {
        const response = await axiosInstance.get("/v1/header-menu?platform=tutor");
        if (response.data && response.data.is_success) {
          setMenuItems(response.data.data || []);
        }
      } catch (error) {
        console.error("Failed to fetch header menu items:", error);
        setMenuItems([]);
      } finally {
        setMenuLoading(false);
      }
    };

    fetchMenuItems();
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Separate menu items into center nav and right-side action buttons
  const centerMenuItems = menuItems.filter((item) => item.group_name !== "signin" && item.group_name !== "register");
  const actionButtons = menuItems.filter((item) => item.group_name === "signin" || item.group_name === "register");

  // For mobile: find the signin and register groups to show the Sign In / Register row
  const signinGroup = menuItems.find((item) => item.group_name === "signin");
  const registerGroup = menuItems.find((item) => item.group_name === "register");

  return (
    <header className="main-header">
      {/* ✅ Left: Logo */}
      <div className="logo">
        <NavLink
          to={process.env.REACT_APP_OLD_HOMESHIKSHA}
          onClick={() => {
            setCurrentPath(() => "/dashboard");
          }}
        >
          <img src="/homeshiksha-logo.svg" alt="logo" />
        </NavLink>
      </div>

      {/* ✅ Logged In → Show Navigation */}
      {isLoggedIn ? (
        !["/", "/register", "/own-a-franchise", "/counselling"].includes(currentPath) &&
        !currentPath?.includes("/wa-login/") &&
        !currentPath?.includes("/tutor-applied/") && (
          <>
            <Navigation></Navigation>
          </>
        )
      ) : (
        <>
          {/* ✅ Center: Menu Nav */}
          {!menuLoading && (
            <nav className="main-nav">
              <ul className="nav-list">
                {centerMenuItems.map((item) => (
                  <MenuItem key={item.id} item={item} />
                ))}
              </ul>
            </nav>
          )}

          {/* ✅ Right: Action Buttons (SignIn / Register) */}
          {!menuLoading && actionButtons.length > 0 && (
            <div className="header-actions">
              <ul className="nav-list action-list">
                {actionButtons.map((item) => (
                  <MenuItem key={item.id} item={item} />
                ))}
              </ul>
            </div>
          )}

          {/* ✅ Mobile Menu */}
          <div className="mobile-menu-icon" onClick={toggleMobileMenu}>
            <img src="menu.svg" alt="hamberger_menu" />
          </div>

          {isMobileMenuOpen && !menuLoading && (
            <nav className="mobile-nav">
              <ul>
                {/* Mobile Sign In / Register row */}
                <li className="mobile-li">
                  {signinGroup && signinGroup.children && signinGroup.children.length > 0 && (
                    <>
                      <MenuLink item={{ ...signinGroup.children[signinGroup.children.length - 1], label: "Sign In" }} />
                      /
                    </>
                  )}
                  {registerGroup && registerGroup.children && registerGroup.children.length > 0 ? (
                    <MenuLink
                      item={{ ...registerGroup.children[registerGroup.children.length - 1], label: "Register" }}
                    />
                  ) : (
                    <Link to="/register">Register</Link>
                  )}
                </li>

                {/* Render all menu items except signin and register groups */}
                {centerMenuItems.map((item) => (
                  <MenuItem key={`mobile-${item.id}`} item={item} />
                ))}
              </ul>
            </nav>
          )}
        </>
      )}
    </header>
  );
};

export default Header;
